import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.css';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import cn from 'classnames';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';

const EditableSelect = ({
    options = [],
    value,
    title,
    width = 200,
    setFieldValue,
    valuesName,
    disabled = false,
    error,
    className,
    withoutAll = false,
    placeholder = 'Выбрать',
    allowCustomValue = true,
    onSearch,
    highlight = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [customOptions, setCustomOptions] = useState([]);

    const selectRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const isEditingRef = useRef(false);

    // Объединяем переданные options с добавленными пользователем
    const allOptions = useMemo(() => {
        const combined = [...(options || [])];
        customOptions.forEach(custom => {
            if (!combined.some(opt => opt.value === custom.value)) {
                combined.push(custom);
            }
        });
        return combined;
    }, [options, customOptions]);

    // Мемоизированные данные с опцией "Выбрать все"
    const data = useMemo(() => {
        if (!allOptions || allOptions.length === 0) return [];
        return withoutAll ? allOptions : [{ name: 'Выбрать все', value: 0 }, ...allOptions];
    }, [allOptions, withoutAll]);

    // Показываем все опции без фильтрации
    const displayedOptions = data;

    // Синхронизация inputValue с внешним value (только если не редактируем)
    useEffect(() => {
        if (isEditingRef.current) return;

        if (value !== undefined && value !== null && value !== '') {
            const selectedOption = allOptions?.find(item => item.value == value);
            setInputValue(selectedOption?.name || value);
        } else {
            setInputValue('');
        }
    }, [value, allOptions]);

    // Сброс highlightedIndex при изменении фильтрованных опций
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [displayedOptions.length]);

    // Прокрутка к выделенному элементу
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('li');
            const highlightedItem = items[highlightedIndex];
            if (highlightedItem) {
                highlightedItem.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleOpen = useCallback(() => {
        if (!disabled) {
            setIsOpen(true);
            setHighlightedIndex(-1);
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [disabled]);

    const handleClose = useCallback(() => {
        if (!isOpen) return;

        isEditingRef.current = false;
        setIsOpen(false);
        setHighlightedIndex(-1);
        // Сохраняем введённое значение при закрытии
        if (allowCustomValue && inputValue.trim()) {
            const trimmedValue = inputValue.trim();
            const existsInOptions = allOptions.some(opt => opt.name === trimmedValue || opt.value === trimmedValue);

            // Добавляем в список если новое значение
            if (!existsInOptions) {
                setCustomOptions(prev => {
                    if (prev.some(opt => opt.value === trimmedValue)) return prev;
                    return [...prev, { name: trimmedValue, value: trimmedValue }];
                });
            }

            if (setFieldValue) {
                setFieldValue(valuesName, trimmedValue);
            }
        } else if (!inputValue.trim() && value !== undefined && value !== null) {
            // Восстанавливаем отображаемое значение если поле пустое
            const selectedOption = allOptions?.find(item => item.value == value);
            setInputValue(selectedOption?.name || '');
        }
    }, [isOpen, value, allOptions, allowCustomValue, inputValue, setFieldValue, valuesName]);

    const handleSelect = useCallback((optionValue, optionName, isCustom = false) => {
        isEditingRef.current = false;
        setInputValue(optionName);
        setIsOpen(false);
        setHighlightedIndex(-1);

        // Если это новое значение - добавляем в список
        if (isCustom) {
            const newOption = { name: optionName, value: optionValue };
            setCustomOptions(prev => {
                if (prev.some(opt => opt.value === optionValue)) return prev;
                return [...prev, newOption];
            });
        }

        if (setFieldValue) {
            setFieldValue(valuesName, optionValue);
        }
    }, [setFieldValue, valuesName]);

    const handleInputChange = useCallback((e) => {
        const newValue = e.target.value;
        isEditingRef.current = true;
        setInputValue(newValue);
        setHighlightedIndex(-1);

        if (!isOpen) {
            setIsOpen(true);
        }

        if (onSearch) {
            onSearch(newValue);
        }
    }, [isOpen, onSearch]);

    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                e.preventDefault();
                handleOpen();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < displayedOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : displayedOptions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && displayedOptions[highlightedIndex]) {
                    const option = displayedOptions[highlightedIndex];
                    handleSelect(option.value, option.name, false);
                } else if (inputValue.trim()) {
                    // Добавляем новое значение в список и выбираем его
                    const trimmedValue = inputValue.trim();
                    const existsInOptions = allOptions.some(opt => opt.name === trimmedValue || opt.value === trimmedValue);
                    handleSelect(trimmedValue, trimmedValue, !existsInOptions);
                }
                break;
            case 'Escape':
                e.preventDefault();
                handleClose();
                break;
            case 'Tab':
                handleClose();
                break;
            default:
                break;
        }
    }, [isOpen, highlightedIndex, displayedOptions, handleOpen, handleClose, handleSelect, inputValue, allOptions]);

    const handleItemClick = useCallback((e, option) => {
        e.preventDefault();
        e.stopPropagation();
        handleSelect(option.value, option.name);
    }, [handleSelect]);

    useOnClickOutside(selectRef, handleClose);

    const containerClass = cn(styles.container, className);
    const inputClass = cn(styles.input, {
        [styles.inputError]: error,
        [styles.inputDisabled]: disabled,
    });

    return (
        <div
            className={containerClass}
            ref={selectRef}
            style={{ width: width + 20 }}
        >
            {title && <label className={cn(styles.label, { [styles.greenLabel]: highlight })}>{title}</label>}

            <div className={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    className={inputClass}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleOpen}
                    placeholder={placeholder}
                    disabled={disabled}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-controls={`${valuesName}-listbox`}
                    aria-activedescendant={
                        highlightedIndex >= 0
                            ? `${valuesName}-option-${highlightedIndex}`
                            : undefined
                    }
                    autoComplete="off"
                />
                <button
                    type="button"
                    className={styles.toggleButton}
                    onClick={isOpen ? handleClose : handleOpen}
                    disabled={disabled}
                    tabIndex={-1}
                    aria-label={isOpen ? 'Закрыть список' : 'Открыть список'}
                >
                    {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </button>
            </div>

            {isOpen && !disabled && (
                <ul
                    ref={listRef}
                    className={styles.optionsList}
                    role="listbox"
                    id={`${valuesName}-listbox`}
                    aria-label={title || 'Выбор значения'}
                >
                    {displayedOptions.length > 0 ? (
                        displayedOptions.map((option, index) => (
                            <li
                                key={`${option.value}-${valuesName}`}
                                id={`${valuesName}-option-${index}`}
                                className={cn(styles.option, {
                                    [styles.optionHighlighted]: index === highlightedIndex,
                                    [styles.optionSelected]: option.value == value,
                                })}
                                onClick={(e) => handleItemClick(e, option)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                role="option"
                                aria-selected={option.value == value}
                            >
                                {option.name}
                            </li>
                        ))
                    ) : (
                        <li className={styles.noResults}>
                            Нет элементов
                        </li>
                    )}
                </ul>
            )}

            {error && <span className={styles.errorText}>{error}</span>}
        </div>
    );
};

export default EditableSelect;
